export default {
  namespaced: true,
  state: {
    dictionary: {},
    queue: -50,
    counter: 0,
  },
  mutations: {
    setTranslation(state, data) {
      state.dictionary[data.key] = data.value;
    },
  },
  actions: {
    async actionTranslatePhrase(
      {commit, dispatch, state, rootState},
      sourcePhrase,
    ) {
      function sleep(ms) {
        return new Promise((resolve) => setTimeout(resolve, ms));
      }

      if (!rootState.user?.preferences?.locale) {
        do {
          state.counter += 1;
          await sleep(100);
        } while (!rootState.user?.preferences?.locale && state.counter < 50);
      }

      const locale = rootState.user?.preferences?.locale || 'fr-FR';

      if (locale === 'fr-FR') {
        // avoid call  if no need to translate
        return sourcePhrase;
      }
      state.queue += 50; // to avoid DDos with the same requests
      await sleep(state.queue);

      const savedTranslation = state.dictionary[`${sourcePhrase}_${locale}`];
      if (savedTranslation) {
        // read from cache
        state.queue -= 50;
        return savedTranslation;
      }
      try {
        const translatedPhrase = await dispatch(
          'actionWeglotTranslate',
          sourcePhrase,
        );
        state.queue -= 50;
        commit('setTranslation', {
          key: `${sourcePhrase}_${locale}`,
          value: translatedPhrase,
        });
        return translatedPhrase;
      } catch (error) {
        console.error('Translation failed:', error);
        return sourcePhrase; // Fallback to source phrase on error
      }
    },
    /**
     * Translate text from an arbitrary source language into the user's locale
     * via Weglot's translation REST API. Unlike actionTranslatePhrase (whose
     * source language is always French), the source language is explicit here,
     * so it can translate content coming from any provider (English today,
     * Spanish tomorrow, …) — including into French.
     *
     * @param {{text: string|string[], from: string}} payload
     *   - text: a single phrase or an array of phrases.
     *   - from: ISO 2-letter source language code (e.g. 'en', 'es').
     * @returns {Promise<string|string[]>} same shape as `text`. Results are
     *   cached in the shared dictionary and the source text is returned on any
     *   failure.
     */
    async actionTranslateText({state, commit, rootState}, payload) {
      const {text, from} = payload || {};
      const phrases = Array.isArray(text) ? text : [text];
      const localeFull = rootState.user?.preferences?.locale || 'fr-FR';
      const lTo = localeFull.substring(0, 2).toLowerCase();
      const lFrom = (from || '').toLowerCase();

      // Unknown source or already in the target language: nothing to do.
      if (!lFrom || lTo === lFrom) return text;

      const cacheKey = (p) => `${lFrom}:${p}_${lTo}`;
      const results = new Array(phrases.length);
      const missing = [];
      const missingIdx = [];
      phrases.forEach((p, i) => {
        if (!p) {
          results[i] = p;
          return;
        }
        const cached = state.dictionary[cacheKey(p)];
        if (cached != null) {
          results[i] = cached;
        } else {
          missing.push(p);
          missingIdx.push(i);
        }
      });

      if (missing.length) {
        try {
          const apiKey =
            import.meta.env.VITE_APP_ENV === 'live'
              ? 'wg_8ab3aff22b808085d046f8eebec9f4a42'
              : 'wg_f1ae7d22c57dbf2ab34503156d75c9668';
          const response = await fetch(
            `https://api.weglot.com/translate?api_key=${apiKey}`,
            {
              method: 'POST',
              headers: {'Content-Type': 'application/json'},
              body: JSON.stringify({
                l_from: lFrom,
                l_to: lTo,
                request_url:
                  typeof window !== 'undefined' ? window.location?.href : '',
                words: missing.map((w) => ({w, t: 1})),
              }),
            },
          );
          const data = await response.json();
          const toWords = Array.isArray(data?.to_words) ? data.to_words : [];
          missing.forEach((p, j) => {
            const translated = toWords[j] || p;
            commit('setTranslation', {key: cacheKey(p), value: translated});
            results[missingIdx[j]] = translated;
          });
        } catch (error) {
          console.error('Text translation failed:', error);
          missingIdx.forEach((idx, j) => {
            results[idx] = missing[j];
          });
        }
      }

      return Array.isArray(text) ? results : results[0];
    },
    /**
     * Wrapper function for window.Weglot.translate
     * @param {string} sourcePhrase - The phrase to be translated
     * @returns {Promise<string>} - A promise that resolves to the translated phrase or source phrase
     */
    async actionWeglotTranslate(_commit, sourcePhrase) {
      return new Promise((resolve) => {
        if (!window.Weglot || !window.Weglot.translate) {
          console.error('Weglot is not initialized.');
          resolve(sourcePhrase); // Fallback to source phrase
          return;
        }

        window.Weglot.translate(
          {
            words: [{t: 1, w: sourcePhrase}],
          },
          (data) => {
            if (data && data[0].length > 0) {
              resolve(data[0]); // Return the translated phrase
            } else {
              resolve(sourcePhrase); // Fallback to source phrase if no translation
            }
          },
        );
      });
    },
  },
};
